import { InputAdornment, Stack, Typography } from '@mui/material';

import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Briefcase02Icon } from '@/components/icons/Briefcase02Icon';
import { Card } from '@/components/layout/Card/Card';
import {
  ColumnWidths,
  FormLayoutItem,
  FormLayoutRow,
} from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { useReportError } from '@/hooks/useReportError';
import { useSubBrandOptions } from '@/modules/admin/branding/hooks/useSubBrandOptions';
import {
  useAssetProviderDataLinkingState,
  useAssetProviderDisplayName,
} from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { IntegrationClientsTypeahead } from '@/modules/assetProviderIntegrations/shared/IntegrationClientTypeahead/IntegrationClientsTypeahead';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { RelationshipOwner } from '@/modules/content/tooltipContent/RelationshipOwner';
import { useCreateHouseholdV2ModalQueryQuery } from '@/modules/createHousehold/CreateHouseholdV2Modal/graphql/CreateHouseholdV2Modal.generated';
import { PossibleRelationshipOwnersTypeahead } from '@/modules/household/components/PossibleRelationshipOwnersTypeahead/PossibleRelationshipOwnersTypeahead';
import { TeamsTypeahead } from '@/modules/teams/components/TeamsTypeahead/TeamsTypeahead';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { FieldNameFromFormShape } from '@/types/react-hook-form';
import { getNodes } from '@/utils/graphqlUtils';

import { ClientSetupFormShape } from './ClientSetup.type';

/**
 * The bordered "Additional details" section of the client-setup step:
 * relationship owner, sub-brand, teams, and (when available) an integration
 * data link. Relationship owner and teams always show; sub-brand shows only
 * when the tenant has sub-brands, and the data link only when an asset-provider
 * integration is available. The visible fields split the row width evenly.
 */
export function ClientSetupAdditionalDetails() {
  const { control } = useFormContext<ClientSetupFormShape>();
  const { reportError } = useReportError();

  const { data: subBrandData } = useCreateHouseholdV2ModalQueryQuery({
    onError: (error) =>
      reportError('Failed to fetch sub-brands for client onboarding', error),
  });
  // useSubBrandOptions puts the tenant default first (value ''), which the form
  // already defaults `subBrandId` to — so the field starts on that default.
  const subBrandOptions = useSubBrandOptions(getNodes(subBrandData?.subBrands));
  const hasSubBrandOptions = subBrandOptions.length > 0;

  // Only users who can manage integration data can link a provider client.
  const canManageIntegrationData = useCanManageIntegrationData();
  const assetProviderName = useAssetProviderDisplayName({ isInline: false });
  const { canConnectToIntegrations, integrationsAvailableForDataLinking } =
    useAssetProviderDataLinkingState();
  const hasDataLink =
    canManageIntegrationData &&
    canConnectToIntegrations.length > 0 &&
    !!assetProviderName;
  const hasIntegrationDataAccess = canConnectToIntegrations.some(
    (integration) => integrationsAvailableForDataLinking.includes(integration)
  );

  // Relationship owner and teams always show; sub-brand and data link are
  // conditional. The visible fields share the row evenly (e.g. 1/4 each when a
  // data link is shown alongside sub-brand, 1/3 each without it).
  const visibleFieldCount =
    2 + (hasSubBrandOptions ? 1 : 0) + (hasDataLink ? 1 : 0);
  const columnWidth = (12 / visibleFieldCount) as ColumnWidths;

  return (
    <Card variant="filled-outline" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="title-s">Additional details</Typography>
        <FormLayoutRow>
          {hasSubBrandOptions && (
            <FormLayoutItem width={columnWidth}>
              <FormAwareSelectInput
                label="Sub-brand"
                showEmptyValue={false}
                fieldName={
                  `subBrandId` as FieldNameFromFormShape<ClientSetupFormShape>
                }
                options={subBrandOptions}
                control={control}
                startAdornment={
                  <InputAdornment position="start">
                    <Briefcase02Icon size={16} />
                  </InputAdornment>
                }
              />
            </FormLayoutItem>
          )}
          <FormLayoutItem width={columnWidth}>
            <PossibleRelationshipOwnersTypeahead
              label="Relationship owner"
              required
              fieldName={
                `primaryRelationshipOwnerId` as FieldNameFromFormShape<ClientSetupFormShape>
              }
              control={control}
              contextualHelp={
                <LuminaryThemeProvider>
                  <RelationshipOwner />
                </LuminaryThemeProvider>
              }
            />
          </FormLayoutItem>
          <FormLayoutItem width={columnWidth}>
            <TeamsTypeahead
              label="Teams"
              fieldName={
                `teamIds` as FieldNameFromFormShape<ClientSetupFormShape>
              }
              control={control}
              disableSCIMManagedTeams={false}
            />
          </FormLayoutItem>
          {hasDataLink && (
            <FormLayoutItem width={columnWidth}>
              <IntegrationClientsTypeahead<ClientSetupFormShape>
                control={control}
                fieldName={
                  `integrationClientIds` as FieldNameFromFormShape<ClientSetupFormShape>
                }
                label={`Data link (${assetProviderName})`}
                providers={canConnectToIntegrations}
                notLoggedIn={!hasIntegrationDataAccess}
              />
            </FormLayoutItem>
          )}
        </FormLayoutRow>
      </Stack>
    </Card>
  );
}
